import React, { useEffect } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import TenantCommercialsApi, {
  ITenantCommercialsBackendData
} from 'api/b2b/TenantCommercial'
import OrbitSession from 'hooks/useCookies'

import { commercialTypes, creditTermOptions } from '../contants'
import { TenantDetails } from '..'

import { IAutoCompleteDropdown } from './TDPDetails'

interface IFormInput {
  commercialType: IAutoCompleteDropdown
  availableLimit: string
  outstandingBalance: string
  walletBalance: string
  currency: string
  creditPeriod: IAutoCompleteDropdown
  creditLimit: string
  creditAction: string
  creditAmount: string
}

function Commercials({
  tenantDetails,
  onSaveSuccess
}: {
  tenantDetails: TenantDetails
  onSaveSuccess: (string) => void
}) {
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      commercialType: commercialTypes[0],
      availableLimit: '',
      outstandingBalance: '',
      walletBalance: '',
      currency: 'USA',
      creditPeriod: creditTermOptions[0],
      creditLimit: '',
      creditAction: 'addCredit',
      creditAmount: ''
    }
  })

  const faberToken = OrbitSession.get('faberToken')

  const { mutate: createTenantCatalog } = useMutation(
    (data: ITenantCommercialsBackendData) =>
      TenantCommercialsApi.createTenantCommercials(data, faberToken),
    {
      onSuccess: () => {
        toast.success('Commercial set successfull')
        onSaveSuccess('#rebate')
      },
      onError: (error) => {
        console.error('API Error:', error)
        toast.error('Failed to create commercials')
      }
    }
  )

  useEffect(() => {
    if (tenantDetails?.currency) {
      setValue('currency', tenantDetails.currency)
    }
  }, [tenantDetails, setValue])

  // ToDO:From where did the remaining field value will come
  // ToDO:Is the add and set credit action is correct?

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const payload: ITenantCommercialsBackendData = {
      tenant_id: tenantDetails.tenantId,
      commercial_type: data.commercialType.value.toUpperCase(),
      credit_terms_period: data.creditPeriod.value,
      credit_limit: data.creditLimit,
      is_active: true,
      created_by: OrbitSession.get('email') || '',
      updated_by: OrbitSession.get('email') || ''
    }
    // Add the existing credit limit if the action is to add credit
    data.creditAction === 'addCredit'
      ? (payload.credit_limit += data.creditAmount)
      : (payload.credit_limit = data.creditAmount)
    createTenantCatalog(payload)
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Commercial Type*</Typography>
          <Controller
            name='commercialType'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                options={commercialTypes}
                onChange={(_, data) => field.onChange(data)}
                size='small'
                sx={{ width: '20%' }}
                renderInput={(params) => <TextField {...params} />}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Currency*</Typography>
          <Controller
            name='currency'
            control={control}
            rules={{
              required: { value: true, message: 'Currency is Required' }
            }}
            render={({ field }) => (
              <TextField
                id='currency'
                variant='outlined'
                size='small'
                disabled={true}
                {...field}
                error={!!errors.currency}
                helperText={errors.currency?.message}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Credit Terms Period*</Typography>
          <Controller
            name='creditPeriod'
            control={control}
            rules={{ required: { value: true, message: 'Required' } }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                disablePortal
                disableClearable
                options={creditTermOptions}
                onChange={(_, data) => field.onChange(data)}
                size='small'
                sx={{ width: '20%' }}
                renderInput={(params) => <TextField {...params} />}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Credit Limit</Typography>
          <Controller
            name='creditLimit'
            control={control}
            render={({ field }) => (
              <TextField
                id='creditLimit'
                variant='outlined'
                size='small'
                disabled={true}
                {...field}
              />
            )}
          />
        </Box>

        <Grid container xs={12}>
          <Grid item md={4} xs={12}>
            <FormControl component='fieldset' margin='normal'>
              <Controller
                name='creditAction'
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      value='addCredit'
                      control={<Radio />}
                      label='Add Credit'
                    />
                    <FormControlLabel
                      value='setCredit'
                      control={<Radio />}
                      label='Set Credit Limit'
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Grid>
          <Grid item md={4} xs={12}>
            <Box display='flex' alignItems='center'>
              <Typography sx={{ mr: '10px' }}>Amount</Typography>
              <Controller
                name='creditAmount'
                control={control}
                render={({ field }) => (
                  <TextField
                    type='number'
                    id='creditAmount'
                    variant='outlined'
                    size='small'
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Wallet Balance</Typography>
          <TextField
            id='outlined-basic'
            variant='outlined'
            size='small'
            value={getValues('walletBalance')}
            disabled={true}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Outstanding Balance</Typography>
          <TextField
            id='outlined-basic'
            variant='outlined'
            size='small'
            value={getValues('outstandingBalance')}
            disabled={true}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px' }}>Available Limit</Typography>
          <TextField
            id='outlined-basic'
            variant='outlined'
            size='small'
            value={getValues('availableLimit')}
            disabled={true}
          />
        </Box>

        <Box sx={{ mt: '50px' }}>
          <Button type='submit' variant='contained' disableElevation>
            Save
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default Commercials
